import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type StickyNote = {
  id: string;
  /** ビューポート X 座標 */
  x: number;
  /** ビューポート Y 座標 */
  y: number;
  /** 付箋の幅 */
  width: number;
  /** 付箋の高さ */
  height: number;
  /** 作成時のスクロール X */
  scrollX: number;
  /** 作成時のスクロール Y */
  scrollY: number;
  /** ページの pathname */
  url: string;
  /** テキスト内容 */
  content: string;
  /** 作成日時（ISO文字列） */
  createdAt: string;
};

type StickyNoteState = {
  notes: StickyNote[];
  /** 付箋の表示/非表示 */
  isVisible: boolean;
  /** 付箋追加モード */
  isAddMode: boolean;
  addNote: (note: Omit<StickyNote, 'id' | 'createdAt'>) => void;
  removeNote: (id: string) => void;
  updateNote: (id: string, updates: Partial<StickyNote>) => void;
  toggleVisibility: () => void;
  setAddMode: (mode: boolean) => void;
};

/**
 * デバッグ用付箋の状態を管理するZustandストア
 * localStorage に永続化し、ブラウザ再起動後も付箋を保持する
 */
export const useStickyNoteStore = create<StickyNoteState>()(
  persist(
    (set) => ({
      notes: [],
      isVisible: true,
      isAddMode: false,
      addNote: (note) =>
        set((state) => ({
          notes: [
            ...state.notes,
            { ...note, id: crypto.randomUUID(), createdAt: new Date().toISOString() },
          ],
        })),
      removeNote: (id) => set((state) => ({ notes: state.notes.filter((n) => n.id !== id) })),
      updateNote: (id, updates) =>
        set((state) => ({
          notes: state.notes.map((n) => (n.id === id ? { ...n, ...updates } : n)),
        })),
      toggleVisibility: () => set((state) => ({ isVisible: !state.isVisible })),
      setAddMode: (mode) => set({ isAddMode: mode }),
    }),
    {
      name: 'debug-sticky-notes',
      version: 1,
      partialize: (state) => ({
        notes: state.notes,
        isVisible: state.isVisible,
      }),
    },
  ),
);
